import React, { useEffect, useState } from 'react';
import { adminApi, API_BASE } from '../../lib/api';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Download, Loader2, Trash2, Eye, FileText } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const TYPES = [
  { value: 'all', label: 'All Submissions' },
  { value: 'loan', label: 'Loan Applications' },
  { value: 'newsletter', label: 'Newsletter Signups' },
  { value: 'contact', label: 'Contact / Other' },
  { value: 'unsubscribe', label: 'Unsubscribes' },
];

export default function AdminSubmissions() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get('type') || 'all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listSubmissions(type === 'all' ? null : type);
      setItems(data || []);
    } catch { toast({ title: 'Failed to load submissions' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  useEffect(() => {
    const p = params.get('type');
    if (p && p !== type) setType(p);
  }, [params]);

  const handleType = (v) => { setType(v); setParams(v === 'all' ? {} : { type: v }); };

  const handleDownload = async () => {
    const url = adminApi.exportCsv(type === 'all' ? null : type);
    const token = localStorage.getItem('taa_token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast({ title: 'Download failed' });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `submissions_${type}_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleDelete = async (s) => {
    if (!window.confirm('Delete this submission?')) return;
    try { await adminApi.deleteSubmission(s.id); await load(); toast({ title: 'Deleted' }); }
    catch { toast({ title: 'Delete failed' }); }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleString() : '';
  const preview = (data) => {
    if (!data) return '';
    const keys = Object.keys(data);
    if (keys.length === 0) return '';
    const first = keys.slice(0, 3).map((k) => `${k}: ${data[k]}`).join(' • ');
    return keys.length > 3 ? `${first} …` : first;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-900">Form Submissions</h1>
          <p className="text-slate-600 mt-1">All form submissions from the public site. Export as CSV / Excel.</p>
        </div>
        <div className="flex gap-3 items-center">
          <Select value={type} onValueChange={handleType}>
            <SelectTrigger className="w-56 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleDownload} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-500">
          <FileText className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          No submissions yet.
        </div>
      ) : (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Preview</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">{s.type}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-md truncate">{preview(s.data)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setView(s)}><Eye className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(s)} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(o) => { if (!o) setView(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Submission Details</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">
                <div><span className="font-semibold text-slate-700">Type:</span> {view.type}</div>
                <div><span className="font-semibold text-slate-700">Date:</span> {fmtDate(view.created_at)}</div>
                <div><span className="font-semibold text-slate-700">ID:</span> {view.id}</div>
              </div>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(view.data || {}).map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-100 last:border-0">
                        <td className="pr-3 py-2 font-semibold text-slate-700 align-top w-1/3">{k}</td>
                        <td className="py-2 text-slate-700 break-all">{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
